import React, { useEffect, useMemo, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

export const resolveMediaUrl = (url) => {
  if (!url) return "";
  const value = String(url).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) {
    return value;
  }
  // Uploaded files are served by the API. Other root paths are client public assets.
  if (value.startsWith("/") && !value.startsWith("/uploads") && !value.startsWith("/api/")) {
    return value;
  }
  return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
};

/** Auto-created labels ("Slice 1") are not the designed prize name. */
export const isPlaceholderSliceLabel = (text) =>
  /^slice\s*\d+$/i.test(String(text || "").trim());

export const sliceHasArt = (slice) =>
  !!(slice?.background_image_url || slice?.image_url);

const prepareImage = (img, rotation) => {
  const degrees = Number(rotation) || 0;
  if (!degrees) return img;
  const size = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.translate(size / 2, size / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(img, -(img.naturalWidth || img.width) / 2, -(img.naturalHeight || img.height) / 2);
  return canvas;
};

const drawCover = (ctx, source, cx, cy, diameter) => {
  const iw = source.naturalWidth || source.width;
  const ih = source.naturalHeight || source.height;
  if (!iw || !ih) return;
  const scale = Math.max(diameter / iw, diameter / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(source, cx - dw / 2, cy - dh / 2, dw, dh);
};

/**
 * Static face of a configured prize wheel: slice colors, background images,
 * and a single wheel background image. Slice 0 sits at the top, matching the
 * live pointer and spin math.
 */
const ConfiguredWheelFace = ({
  slices = [],
  size = 280,
  backgroundImageUrl = "",
  centerLogo = "",
  framed = true,
  fill = false,
}) => {
  const canvasRef = useRef(null);
  const imagesRef = useRef({});
  const [loadGen, setLoadGen] = useState(0);

  const model = useMemo(() => {
    const items = (slices || []).map((slice, index) => ({
      color: slice?.color || slice?.slice_color || "#3a3a3a",
      text: (slice?.text ?? slice?.display_name ?? "").trim(),
      imageUrl: resolveMediaUrl(slice?.background_image_url || slice?.image_url || ""),
      zoom: slice?.image_zoom ?? 0.6,
      rotation: slice?.image_rotation ?? 0,
      offsetX: slice?.image_offset_x ?? 0,
      offsetY: slice?.image_offset_y ?? 0,
      index,
    }));
    return {
      items,
      wheelImageUrl: resolveMediaUrl(backgroundImageUrl),
      logoUrl: resolveMediaUrl(centerLogo),
      anySliceImage: items.some((item) => item.imageUrl),
    };
  }, [slices, backgroundImageUrl, centerLogo]);

  useEffect(() => {
    let cancelled = false;
    const urls = [
      ...model.items.map((item) => item.imageUrl),
      model.wheelImageUrl,
      model.logoUrl,
    ].filter(Boolean);
    [...new Set(urls)].forEach((url) => {
      const cached = imagesRef.current[url];
      if (cached?.complete && cached.naturalWidth) return;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (cancelled) return;
        imagesRef.current = { ...imagesRef.current, [url]: img };
        setLoadGen((gen) => gen + 1);
      };
      img.onerror = () => {};
      img.src = url;
    });
    return () => {
      cancelled = true;
    };
  }, [model]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(size * dpr));
    canvas.height = Math.max(1, Math.round(size * dpr));
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = size / 2;
    const cy = size / 2;
    const outside = size / 2 - (framed ? 8 : 2);
    // Live wheel overlays its own center logo, so the hole stays just inside that button.
    const inside = model.logoUrl ? outside * 0.28 : outside * 0.13;
    const count = Math.max(model.items.length, 1);
    const arc = (2 * Math.PI) / count;
    const startBase = -Math.PI / 2;
    const wheelImg = model.wheelImageUrl ? imagesRef.current[model.wheelImageUrl] : null;
    const useWheelImage =
      !model.anySliceImage && wheelImg?.complete && wheelImg.naturalWidth > 0;

    ctx.clearRect(0, 0, size, size);

    if (useWheelImage) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, outside, 0, Math.PI * 2);
      ctx.clip();
      drawCover(ctx, wheelImg, cx, cy, outside * 2);
      ctx.restore();
      ctx.strokeStyle = "rgba(0,0,0,0.45)";
      ctx.lineWidth = 1.5;
      for (let i = 0; i < count; i += 1) {
        const angle = startBase + i * arc;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * inside, cy + Math.sin(angle) * inside);
        ctx.lineTo(cx + Math.cos(angle) * outside, cy + Math.sin(angle) * outside);
        ctx.stroke();
      }
    } else {
      for (let i = 0; i < count; i += 1) {
        const start = startBase + i * arc;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, outside, start, start + arc, false);
        ctx.closePath();
        ctx.fillStyle = model.items[i]?.color || "#3a3a3a";
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.35)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      model.items.forEach((item, i) => {
        if (!item.imageUrl) return;
        const loaded = imagesRef.current[item.imageUrl];
        if (!loaded?.complete || !loaded.naturalWidth) return;
        const prepared = prepareImage(loaded, item.rotation);
        const start = startBase + i * arc;
        const mid = start + arc / 2;
        const imgH = size * 0.34 * (Number(item.zoom) || 0.6);
        const srcW = prepared.naturalWidth || prepared.width;
        const srcH = prepared.naturalHeight || prepared.height;
        const imgW = srcH ? (srcW / srcH) * imgH : imgH;
        const contentR = outside * 0.62;
        const contentX = cx + Math.cos(mid) * contentR;
        const contentY = cy + Math.sin(mid) * contentR;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, outside - 2, start, start + arc, false);
        ctx.closePath();
        ctx.clip();
        ctx.translate(contentX, contentY);
        ctx.rotate(mid + Math.PI / 2);
        const drawX = (imgW + (Number(item.offsetX) || 0)) / -2;
        const drawY = -(imgH + (Number(item.offsetY) || 0)) / 2;
        ctx.drawImage(prepared, drawX, drawY, imgW, imgH);
        ctx.restore();
      });
    }

    model.items.forEach((item, i) => {
      const hasImage = !!item.imageUrl || useWheelImage;
      if (!item.text) return;
      if (hasImage && isPlaceholderSliceLabel(item.text)) return;
      const start = startBase + i * arc;
      const mid = start + arc / 2;
      const labelR = outside * (item.imageUrl ? 0.78 : 0.62);
      ctx.save();
      ctx.translate(cx + Math.cos(mid) * labelR, cy + Math.sin(mid) * labelR);
      ctx.rotate(mid + Math.PI / 2);
      ctx.fillStyle = "#fff";
      ctx.font = `700 ${Math.max(9, size * 0.038)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = "rgba(0,0,0,0.75)";
      ctx.shadowBlur = 4;
      const maxWidth = Math.max(36, (arc * outside) * 0.7);
      let label = item.text;
      while (label.length > 1 && ctx.measureText(label).width > maxWidth) {
        label = `${label.slice(0, -2)}…`;
      }
      ctx.fillText(label, 0, 0);
      ctx.restore();
    });

    if (framed) {
      ctx.beginPath();
      ctx.arc(cx, cy, outside, 0, Math.PI * 2);
      ctx.strokeStyle = "#FFD700";
      ctx.lineWidth = Math.max(4, size * 0.025);
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.arc(cx, cy, inside, 0, Math.PI * 2);
    ctx.fillStyle = "#111";
    ctx.fill();
    ctx.strokeStyle = "rgba(255,215,0,0.85)";
    ctx.lineWidth = 3;
    ctx.stroke();

    const logo = model.logoUrl ? imagesRef.current[model.logoUrl] : null;
    if (logo?.complete && logo.naturalWidth) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, inside - 4, 0, Math.PI * 2);
      ctx.clip();
      drawCover(ctx, logo, cx, cy, (inside - 4) * 2);
      ctx.restore();
    }
  }, [model, size, framed, loadGen]);

  return (
    <canvas
      ref={canvasRef}
      style={
        fill
          ? { position: "absolute", inset: 0, width: "100%", height: "100%" }
          : {
              width: size,
              height: size,
              display: "block",
              margin: "0 auto",
              maxWidth: "100%",
            }
      }
      aria-label="Configured prize wheel"
    />
  );
};

export default ConfiguredWheelFace;
